/**
 * GraphQL Client for Deployment API
 *
 * Uses existing axios instance for:
 * - Automatic JWT token injection
 * - Error handling and retry logic
 */

import axiosInstance from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { getDefaultOrgId } from '@/utils/defaultOrg';

function readBrowserOrgId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('org_id');
    if (stored) return stored;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith('org_id='));
    return cookie ? decodeURIComponent(cookie.slice('org_id='.length)) : null;
  } catch {
    return null;
  }
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserOrgId();
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

/** Prefer an `organizationId` GraphQL variable when the caller passed one, else fall back to the selected org. */
function graphqlAuthHeaders(variables?: Record<string, any>): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const varOrgId = typeof variables?.organizationId === 'string' ? variables.organizationId : null;
  const effectiveOrgId = varOrgId || resolveCurrentOrgId(state);
  const headers: Record<string, string> = {};
  if (effectiveOrgId) headers['X-Organization-ID'] = effectiveOrgId;
  if (state.user?.id) headers['X-User-ID'] = state.user.id;
  return headers;
}

export async function graphqlRequest<T = any>(
  query: string,
  variables?: Record<string, any>
): Promise<T> {
  try {
    const response = await axiosInstance.post(
      '/graphql',
      { query, variables },
      { headers: graphqlAuthHeaders(variables) }
    );

    // Check _originalResponse first (axios interceptor unwraps data)
    const originalResponse = (response as any)._originalResponse;
    if (originalResponse?.errors?.length) {
      throw new Error(
        originalResponse.errors.map((e: any) => (e.message || '').replace(/^"|"$/g, '')).join(', ')
      );
    }

    // Handle GraphQL errors in response.data
    if (response.data?.errors?.length) {
      throw new Error(
        response.data.errors.map((e: any) => (e.message || '').replace(/^"|"$/g, '')).join(', ')
      );
    }

    return response.data?.data || response.data;
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errs = error.response.data.errors;
      const msg = Array.isArray(errs)
        ? errs.map((e: any) => (e.message || '').replace(/^"|"$/g, '')).join(', ')
        : typeof errs === 'string' ? errs : JSON.stringify(errs);
      throw new Error(msg);
    }
    throw error;
  }
}

export async function graphqlMutation<T = any>(
  mutation: string,
  variables?: Record<string, any>
): Promise<T> {
  return graphqlRequest<T>(mutation, variables);
}
